import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectLanesTiers,
  selectProductGroups,
  selectAudiences,
  selectCategories,
  selectFeatures,
  selectGenAiFilter,
  selectProductFilter,
  selectStatus,
  selectRevenueImpactFilter,
  selectSearchFilterActive,
  selectSearchFilteredItems,
  selectMomentTypeFilter
} from 'app/pages/Home/slice/selectors';
import type { RootState } from 'types';
import { subProjectMatchesMomentTypeSelection } from 'app/components/Header/momentTypeRoadmapFilter';
import { RISK_LEVELS } from 'utils/constants';

interface Project {
  title: string;
  issueId?: string;
  labels: string[];
  links: Array<{ type: string; reference: string }>;
  releasePhase?: string;
  projectType?: string;
  revenueImpact?: boolean;
  releasePhases?: Array<{
    phaseName: string;
    generalInfo?: {
      revenueImpact?: boolean;
    };
    status?: string;
  }>;
  customerAudiences?: string | string[];
  productGroups?: string | string[];
  categories?: string | string[];
  products?: string | string[];
  features?: Array<{
    featureId?: string;
    issueId?: string;
    surfaces?: string[];
    genai?: boolean;
    consumesCredit?: boolean;
  }>;
  riskLevel?: string;
  surfaces?: string[];
  genai?: boolean;
  consumesCredit?: boolean;
  mustWin?: boolean;
  releasePhaseId?: string;
  showOnTimeline?: boolean;
}

interface ParkingLotItem {
  project: Project[];
}

/**
 * Custom hook to filter parking lot data using the same filter logic as Milestones Grid
 * This ensures consistent filtering across both views
 */
export const useFilteredParkingLot = (
  data?: ParkingLotItem[]
): ParkingLotItem[] | undefined => {
  // Get all filter states from Redux
  const lanesTiers = useSelector(selectLanesTiers);
  const productGroups = useSelector(selectProductGroups);
  const audiences = useSelector(selectAudiences);
  const categories = useSelector(selectCategories);
  const selectedFeatures = useSelector(selectFeatures);
  const genAiFilter = useSelector(selectGenAiFilter);
  const selectedProducts = useSelector(selectProductFilter);
  const status = useSelector(selectStatus);
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const momentTypeFilter = useSelector(selectMomentTypeFilter);
  const rsdMomentTypes = useSelector(
    (s: RootState) => s.rsdForms?.momentTypes ?? []
  );
  const searchFilterActive = useSelector(selectSearchFilterActive);
  const searchFilteredItems = useSelector(selectSearchFilteredItems);

  // Helper functions for filter matching
  const matchesAudience = (project: Project, selected: string[]) => {
    if (selected.length === 0) return true;
    const projectAudiences = project.customerAudiences;
    if (Array.isArray(projectAudiences)) {
      return projectAudiences.some((aud: string) => selected.includes(aud));
    }
    return selected.includes(projectAudiences || '');
  };

  const matchesProductGroup = (project: Project, selected: string[]) => {
    if (selected.length === 0) return true;
    const groups = project.productGroups;
    if (Array.isArray(groups)) {
      return groups.some((g: string) => selected.includes(g));
    }
    return selected.includes(groups || '');
  };

  const matchesCategory = (project: Project, selected: string[]) => {
    if (selected.length === 0) return true;
    const projectCategories = project.categories;
    if (Array.isArray(projectCategories)) {
      return projectCategories.some((c: string) => selected.includes(c));
    }
    return selected.includes(projectCategories || '');
  };

  const matchesProduct = (project: Project, selected: string[]) => {
    if (selected.length === 0) return true;
    const projectProducts = project.products;
    if (Array.isArray(projectProducts)) {
      return projectProducts.some((p: string) => selected.includes(p));
    }
    return selected.includes(projectProducts || '');
  };

  const matchesStatus = (project: Project, selectedStatus: string[]) => {
    if (selectedStatus.length === 0) return true;
    const riskLevel = project.riskLevel;

    return selectedStatus.some(key => {
      if (key === 'all') return true;
      if (key === 'risk-level-low' && riskLevel === RISK_LEVELS.LOW)
        return true;
      if (key === 'risk-level-medium' && riskLevel === RISK_LEVELS.MODERATE)
        return true;
      if (key === 'risk-level-high' && riskLevel === RISK_LEVELS.HIGH)
        return true;
      if (key === 'in-planning' && riskLevel === RISK_LEVELS.IN_PLANNING)
        return true;
      return false;
    });
  };

  const matchesSurface = (project: Project, selectedSurfaces: string[]) => {
    if (selectedSurfaces.length === 0) return true;
    const rootSurfaces = Array.isArray(project.surfaces)
      ? (project.surfaces as string[])
      : [];
    const rootMatch = rootSurfaces.some(s => selectedSurfaces.includes(s));

    const featuresArray = Array.isArray(project.features)
      ? project.features
      : [];
    const featureMatch = featuresArray.some(
      (feature: any) =>
        Array.isArray(feature.surfaces) &&
        feature.surfaces.some((s: string) => selectedSurfaces.includes(s))
    );

    return rootMatch || featureMatch;
  };

  const matchesGenAi = (project: Project, selectedFilters: string[]) => {
    if (selectedFilters.length === 0) return true;

    const hasAllGenAiFilter = selectedFilters.includes('gen-ai-all-features');
    const hasConsumesCreditsFilter = selectedFilters.includes(
      'gen-ai-consumes-credits'
    );

    if (!hasAllGenAiFilter && !hasConsumesCreditsFilter) {
      return true;
    }

    const featuresArray = Array.isArray(project.features)
      ? project.features
      : [];

    const featureMatches = featuresArray.some((feature: any) => {
      const isGenAi = feature.genai === true;
      const consumesCredits = feature.consumesCredit === true;
      if (hasAllGenAiFilter && isGenAi) return true;
      if (hasConsumesCreditsFilter && isGenAi && consumesCredits) return true;
      return false;
    });

    const rootGenAi = project.genai === true;
    const rootConsumesCredits = project.consumesCredit === true;
    const rootMatches =
      (hasAllGenAiFilter && rootGenAi) ||
      (hasConsumesCreditsFilter && rootGenAi && rootConsumesCredits);

    return featureMatches || rootMatches;
  };

  const hasRevenueImpact = (project: Project): boolean => {
    if (project?.revenueImpact === true) {
      return true;
    }
    if (project?.releasePhases && Array.isArray(project.releasePhases)) {
      const activePhase =
        project.releasePhases.find((phase: any) => phase.status === 'ACTIVE') ||
        project.releasePhases[0];
      return activePhase?.generalInfo?.revenueImpact === true;
    }
    return false;
  };

  const matchesSearchFilter = (project: Project) => {
    if (!searchFilterActive || searchFilteredItems.length === 0) {
      return true;
    }

    // Check plain ID match first
    if (searchFilteredItems.includes(project.issueId || '')) {
      return true;
    }

    // Check composite key match for phase-specific filtering
    if (project.releasePhaseId !== undefined) {
      // Try phase-only key first
      const phaseOnlyKey = `${project.issueId}-${project.releasePhaseId}`;
      if (searchFilteredItems.includes(phaseOnlyKey)) {
        return true;
      }

      // Also try with showOnTimeline for backward compatibility
      if (project.showOnTimeline !== undefined) {
        const fullCompositeKey = `${project.issueId}-${project.releasePhaseId}-${project.showOnTimeline}`;
        if (searchFilteredItems.includes(fullCompositeKey)) {
          return true;
        }
      }
    }

    return false;
  };

  // Memoize the filtered data
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return data;
    }

    const effectiveMomentTypes = momentTypeFilter.selectedMomentTypes;

    return data
      .map(item => {
        if (!Array.isArray(item?.project)) {
          return item;
        }

        const filteredProjects = item.project.filter(project => {
          // Search filter supersedes all other filters
          if (searchFilterActive && searchFilteredItems.length > 0) {
            return matchesSearchFilter(project);
          }

          // Apply all other filters with AND logic
          const audienceMatch = matchesAudience(
            project,
            audiences.selectedAudiences
          );
          const productGroupMatch = matchesProductGroup(
            project,
            productGroups.selectedGroups
          );
          const categoryMatch = matchesCategory(
            project,
            categories.selectedCats
          );
          const productMatch = matchesProduct(
            project,
            selectedProducts.selectedProducts
          );

          // Product/Category combined logic: OR if both selected, AND if only one
          const productCategoryMatch =
            (categories.selectedCats.length === 0 &&
              selectedProducts.selectedProducts.length === 0) ||
            (categories.selectedCats.length > 0 &&
            selectedProducts.selectedProducts.length > 0
              ? categoryMatch || productMatch
              : categoryMatch && productMatch);

          const surfaceMatch = matchesSurface(
            project,
            selectedFeatures.selectedFeatures
          );
          const genAiMatch = matchesGenAi(project, genAiFilter.selectedFilters);
          const statusMatch = matchesStatus(project, status.selectedStatus);

          const momentTypeMatch = subProjectMatchesMomentTypeSelection(
            project,
            effectiveMomentTypes,
            rsdMomentTypes
          );

          // Revenue Impact filter
          const revenueImpactFilterActive =
            revenueImpactFilter.selectedFilters.length > 0;
          const revenueImpactMatch =
            !revenueImpactFilterActive ||
            (() => {
              const hasYesFilter =
                revenueImpactFilter.selectedFilters.includes(
                  'revenue-impact-yes'
                );
              const hasNoFilter =
                revenueImpactFilter.selectedFilters.includes(
                  'revenue-impact-no'
                );
              const revenueImpactValue = hasRevenueImpact(project);

              if (hasYesFilter && hasNoFilter) {
                return true;
              }
              if (hasYesFilter && !hasNoFilter) {
                return revenueImpactValue === true;
              }
              if (hasNoFilter && !hasYesFilter) {
                return !revenueImpactValue;
              }
              return true;
            })();

          return (
            audienceMatch &&
            productGroupMatch &&
            productCategoryMatch &&
            surfaceMatch &&
            genAiMatch &&
            statusMatch &&
            momentTypeMatch &&
            revenueImpactMatch
          );
        });

        return {
          ...item,
          project: filteredProjects
        };
      })
      .filter(item => item.project && item.project.length > 0); // Remove empty items
  }, [
    data,
    lanesTiers.selectedLanesTiers,
    productGroups.selectedGroups,
    audiences.selectedAudiences,
    categories.selectedCats,
    selectedProducts.selectedProducts,
    selectedFeatures.selectedFeatures,
    genAiFilter.selectedFilters,
    status.selectedStatus,
    revenueImpactFilter.selectedFilters,
    momentTypeFilter.selectedMomentTypes,
    rsdMomentTypes,
    searchFilterActive,
    searchFilteredItems
  ]);

  return filteredData;
};
